import { TrendingDown, TrendingUp } from 'lucide-react'
import { useState } from 'react'

import type { IncomeStatementLine } from '@/api/reports-types'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { startOfMonth, today } from './constants'
import { BranchFilterField } from './filters'
import { REPORTS_PERMISSIONS, useReportsPermissions } from './permissions'
import { useReportIncomeStatement } from './queries'
import { ReportExportButtons } from './report-export-buttons'
import { ReportHeaderNotice } from './report-header-notice'
import { RestrictedPanel } from './restricted-panel'

/**
 * Income against expense over a period — the profit-and-loss view of the ledger's own account
 * movement.
 *
 * Ranged rather than as-of, like the collections and disbursements registers: a profit figure is a
 * movement between two dates, not a position at one of them. Net profit is read as the server sent
 * it — income less expense, negative on a loss-making period rather than floored at zero — never
 * recomputed from the two tables below, the same reasoning the balance sheet and trial balance
 * screens give for their own totals.
 */
export function IncomeStatementSection() {
  const permissions = useReportsPermissions()

  const [fromDate, setFromDate] = useState(() => startOfMonth(today()))
  const [toDate, setToDate] = useState(today)
  const [branchId, setBranchId] = useState<string | null>(null)

  const runnable = fromDate !== '' && toDate !== '' && fromDate <= toDate
  const query = useReportIncomeStatement(
    { fromDate, toDate, branchId },
    permissions.canViewIncomeStatement && runnable,
  )

  if (!permissions.canViewIncomeStatement) {
    return <RestrictedPanel what="The income statement" permission={REPORTS_PERMISSIONS.trialBalanceView} />
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="grid gap-3 p-4 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="is-from">From</Label>
            <Input id="is-from" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="is-to">To</Label>
            <Input id="is-to" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
          </div>
          <BranchFilterField id="is-branch" branchId={branchId} onChange={setBranchId} />
        </CardContent>
      </Card>

      {!runnable ? (
        <p className="text-xs text-danger-text">The start of the range must not fall after its end.</p>
      ) : query.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : query.isError ? (
        <ErrorSurface error={query.error} onRetry={() => void query.refetch()} title="The income statement could not be read" />
      ) : (
        <>
          <ReportHeaderNotice header={query.data.header} />
          <ReportExportButtons query={{ report: 'IncomeStatement', fromDate, toDate, branchId }} />

          <Alert tone={query.data.netProfit.minor >= 0 ? 'success' : 'danger'}>
            {query.data.netProfit.minor >= 0 ? <TrendingUp aria-hidden /> : <TrendingDown aria-hidden />}
            <div className="min-w-0 space-y-1">
              <AlertTitle>
                {query.data.netProfit.minor >= 0 ? 'This period made a profit' : 'This period made a loss'}
              </AlertTitle>
              <AlertDescription>Income less expense, for the range and scope above.</AlertDescription>
              <div className="grid gap-3 sm:grid-cols-3">
                <TotalStat label="Total income" amount={query.data.totalIncome} />
                <TotalStat label="Total expenses" amount={query.data.totalExpenses} />
                <TotalStat label="Net profit" amount={query.data.netProfit} emphasis />
              </div>
            </div>
          </Alert>

          <div className="grid gap-4 lg:grid-cols-2">
            <IncomeStatementTable title="Income" lines={query.data.incomeLines} total={query.data.totalIncome} totalLabel="Total income" />
            <IncomeStatementTable
              title="Expenses"
              lines={query.data.expenseLines}
              total={query.data.totalExpenses}
              totalLabel="Total expenses"
            />
          </div>
        </>
      )}
    </div>
  )
}

function TotalStat({
  label,
  amount,
  emphasis = false,
}: {
  label: string
  amount: { minor: number; currency: string }
  emphasis?: boolean
}) {
  return (
    <div className="rounded-control border border-border-subtle bg-surface p-3">
      <p className="text-2xs text-muted">{label}</p>
      <MoneyDisplay
        value={amount.minor}
        currency={amount.currency}
        signed={emphasis}
        colourNegative={emphasis}
        emphasis="strong"
        className="text-left"
      />
    </div>
  )
}

function IncomeStatementTable({
  title,
  lines,
  total,
  totalLabel,
}: {
  title: string
  lines: IncomeStatementLine[]
  total: { minor: number; currency: string }
  totalLabel: string
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {lines.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-body">No movement on this side for this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <tr>
                  <TableHead scope="col">Account</TableHead>
                  <TableHead scope="col" className="text-right">Amount</TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {lines.map((line) => (
                  <TableRow key={line.glAccountCode} className={line.isPostable ? undefined : 'bg-surface-muted/60'}>
                    <TableCell style={{ paddingLeft: `${line.level * 12 + 16}px` }}>
                      <span className={line.isPostable ? 'text-sm' : 'text-sm font-semibold text-heading'}>
                        {line.accountName}
                      </span>
                      <span className="ml-2 font-mono text-2xs text-muted">{line.glAccountCode}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <MoneyDisplay value={line.amount.minor} currency={line.amount.currency} symbol={false} />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-surface-muted hover:bg-surface-muted">
                  <TableCell className="text-xs font-semibold text-heading">{totalLabel}</TableCell>
                  <TableCell className="text-right">
                    <MoneyDisplay value={total.minor} currency={total.currency} symbol={false} emphasis="strong" />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
